import { PublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser'
import { loginRequest } from '../config/msalConfig'
import { apiClient } from './api'

class EntraAuthService {
  private msalInstance: PublicClientApplication | null = null

  constructor() {
    // Don't create instance here - it will be set from App.tsx
  }

  /**
   * Set the MSAL instance (called from App.tsx)
   */
  setMsalInstance(instance: PublicClientApplication): void {
    this.msalInstance = instance
  }

  /**
   * Initialize MSAL instance
   */
  async initialize(): Promise<void> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not set. Call setMsalInstance() first.')
    }
    await this.msalInstance.initialize()
  }

  /**
   * Login with redirect
   */
  async loginRedirect(): Promise<void> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized')
    }
    try {
      await this.msalInstance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login redirect failed:', error)
      throw error
    }
  }

  /**
   * Login with popup
   */
  async loginPopup(): Promise<AccountInfo> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized')
    }
    try {
      const response = await this.msalInstance.loginPopup(loginRequest)
      return response.account
    } catch (error) {
      console.error('Login popup failed:', error)
      throw error
    }
  }

  /**
   * Handle redirect result
   */
  async handleRedirectPromise(): Promise<AccountInfo | null> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized')
    }
    try {
      const response = await this.msalInstance.handleRedirectPromise()
      return response?.account || null
    } catch (error) {
      console.error('Handle redirect failed:', error)
      throw error
    }
  }

  /**
   * Get all accounts
   */
  getAllAccounts(): AccountInfo[] {
    if (!this.msalInstance) {
      return []
    }
    return this.msalInstance.getAllAccounts()
  }

  /**
   * Get active account
   */
  getActiveAccount(): AccountInfo | null {
    if (!this.msalInstance) {
      return null
    }
    return this.msalInstance.getActiveAccount()
  }

  /**
   * Set active account
   */
  setActiveAccount(account: AccountInfo | null): void {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized')
    }
    this.msalInstance.setActiveAccount(account)
  }

  /**
   * Get access token silently
   */
  async getTokenSilently(account: AccountInfo): Promise<string> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized')
    }
    try {
      const silentRequest: SilentRequest = {
        scopes: loginRequest.scopes,
        account: account
      }

      const response = await this.msalInstance.acquireTokenSilent(silentRequest)
      return response.idToken
    } catch (error) {
      console.error('Silent token acquisition failed:', error)
      // If silent fails, try interactive
      const response = await this.msalInstance.acquireTokenPopup(loginRequest)
      return response.idToken
    }
  }

  /**
   * Verify token with backend
   */
  async verifyTokenWithBackend(idToken: string): Promise<any> {
    try {
      console.log('🔄 Verifying token with backend...')
      
      const response = await apiClient.post('/auth/verify-token', {
        idToken
      })
      
      if (!response || !response.data) {
        throw new Error('Backend returned empty response')
      }
      
      console.log('✅ Backend verification successful')
      return response.data
    } catch (error) {
      console.error('❌ Backend token verification failed:', error)
      throw error
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized')
    }
    try {
      const account = this.getActiveAccount()
      if (account) {
        await this.msalInstance.logoutRedirect({
          account: account,
          postLogoutRedirectUri: window.location.origin
        })
      }
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const accounts = this.getAllAccounts()
    return accounts.length > 0
  }

  /**
   * Clear any stuck interactions
   */
  clearInteractionInProgress(): void {
    if (!this.msalInstance) {
      return
    }
    try {
      // Clear any stored interaction status from localStorage
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.includes('msal.interaction')) {
          keysToRemove.push(key)
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key))
      
      // Also clear sessionStorage MSAL keys
      const sessionKeysToRemove = []
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && key.includes('msal.interaction')) {
          sessionKeysToRemove.push(key)
        }
      }
      
      sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key))
      
      console.log('✅ Cleared stuck MSAL interactions:', keysToRemove.length + sessionKeysToRemove.length, 'keys removed')
    } catch (error) {
      console.warn('⚠️ Could not clear interactions:', error)
    }
  }

  /**
   * Validate company domain
   */
  validateCompanyDomain(email: string): boolean {
    const allowedDomains = import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS?.split(',') || []
    const domain = email.split('@')[1]?.toLowerCase()
    return allowedDomains.includes(domain)
  }
}

export default new EntraAuthService()